<template>
  <div :id="id" class="chapter-section panel">
    <p class="panel-heading">{{ $t(`chapter4.title`) }}</p>

    <fieldset class="box">
      <legend>
        {{ $t(`chapter4.q0.title`) }}
        <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
          <b-icon
            type="is-info"
            icon="information-variant"
            class="info-toggle"
            :class="{ 'is-active': showInfo_c4q0 }"
            @click.native="showInfo_c4q0 = !showInfo_c4q0"
          />
        </b-tooltip>
        <b-tooltip :label="$t(`general.showRechtstext`)" position="is-right" type="is-warning">
          <b-icon
            type="is-warning"
            icon="format-section"
            class="legal-toggle"
            :class="{ 'is-active': showLegal_c4q0 }"
            @click.native="showLegal_c4q0 = !showLegal_c4q0"
          />
        </b-tooltip>
      </legend>

      <b-field
        :label="$t(`chapter4.q0.text`)"
        :type="store.getFieldError('betroffenePersonen', 'personenGruppen') ? 'is-danger' : ''"
        :message="store.getFieldError('betroffenePersonen', 'personenGruppen')"
      >
        <b-input
          :required="true"
          expanded
          v-model="study.betroffenePersonen.personenGruppen"
          @blur="save"
        />
      </b-field>
      <InfoText v-if="showInfo_c4q0">
        <p>{{ $t('chapter4.q0.info') }}</p>
      </InfoText>
      <RechtsText v-if="showLegal_c4q0">
        {{ $t('rechtstexte.c4q0') }}
      </RechtsText>
    </fieldset>

    <fieldset class="box">
      <legend>{{ $t(`chapter4.q1.title`) }}</legend>
      <b-field
        :label="$t(`chapter4.q1.text`)"
        :type="store.getFieldError('betroffenePersonen', 'personenGruppenTyp') ? 'is-danger' : ''"
        :message="store.getFieldError('betroffenePersonen', 'personenGruppenTyp')"
      >
        <div v-for="option in personenGruppenTypOptions" :key="option">
          <b-checkbox
            :required="true"
            v-model="study.betroffenePersonen.personenGruppenTyp"
            :native-value="option"
            @change="save"
          >
            {{ $t(`chapter4.q1.${option}`) }}
          </b-checkbox>
        </div>
      </b-field>
    </fieldset>

    <fieldset class="box">
      <legend>
        {{ $t(`chapter4.q2.title`) }}
        <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
          <b-icon
            type="is-info"
            icon="information-variant"
            class="info-toggle"
            :class="{ 'is-active': showInfo_c4q2 }"
            @click.native="showInfo_c4q2 = !showInfo_c4q2"
          />
        </b-tooltip>
      </legend>
      <p>
        {{ $t(`chapter4.q2.text`) }}
        <InfoText v-if="showInfo_c4q2">
          <p>{{ $t('chapter4.q2.info') }}</p>
          <p>{{ $t('chapter4.q2.info2') }}</p>
        </InfoText>
      </p>

      <b-field
        :label="$t(`chapter4.q2.text-intern`)"
        :type="
          store.getFieldError('betroffenePersonen', 'erwarteteFaelleKlinik') ? 'is-danger' : ''
        "
        :message="store.getFieldError('betroffenePersonen', 'erwarteteFaelleKlinik')"
      >
        <b-numberinput
          :required="true"
          v-model="study.betroffenePersonen.erwarteteFaelleKlinik"
          controls-position="compact"
          controls-rounded
          :min="0"
          @update:model-value="save"
        />
      </b-field>
      <b-field :label="$t(`chapter4.q2.text-gesamt`)">
        <b-numberinput
          v-model="study.betroffenePersonen.erwarteteFaelleGesamt"
          controls-position="compact"
          controls-rounded
          :min="0"
          @update:model-value="save"
        />
      </b-field>

      <AbweichungStandardprozess
        v-if="
          study.studienparameter.studientypContainer.studienzentren !== 'monozentrisch' &&
          (study.betroffenePersonen.erwarteteFaelleGesamt === undefined ||
            study.betroffenePersonen.erwarteteFaelleGesamt < 7)
        "
      >
        {{ $t('general.abweichungStandardprozess') }}
      </AbweichungStandardprozess>
    </fieldset>

    <fieldset
      class="box"
      v-if="
        !(
          study.studienparameter.studienaufbau === 'mdrIvdrMpdg' ||
          study.studienparameter.studienaufbau === 'ctrAmg'
        )
      "
    >
      <legend>
        {{ $t(`chapter4.q3.title`) }}
        <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
          <b-icon
            type="is-info"
            icon="information-variant"
            class="info-toggle"
            :class="{ 'is-active': showInfo_c4q3 }"
            @click.native="showInfo_c4q3 = !showInfo_c4q3"
          />
        </b-tooltip>
        <b-tooltip :label="$t(`general.showRechtstext`)" position="is-right" type="is-warning">
          <b-icon
            type="is-warning"
            icon="format-section"
            class="legal-toggle"
            :class="{ 'is-active': showLegal_c4q3 }"
            @click.native="showLegal_c4q3 = !showLegal_c4q3"
          />
        </b-tooltip>
      </legend>

      <InfoText v-if="showInfo_c4q3">{{ $t('chapter4.q3.info') }}</InfoText>

      <b-field class="is-flex is-justify-content-flex-start">
        <b-checkbox
          v-model="study.betroffenePersonen.rechteEingeschraenkt"
          type="is-success"
          @change="onRechteEingeschraenktChange"
        >
          {{ $t(`chapter4.q3.true`) }}
        </b-checkbox>
      </b-field>

      <b-field
        v-if="study.betroffenePersonen.rechteEingeschraenkt"
        :label="$t(`chapter4.q3.grund`)"
        :type="
          store.getFieldError('betroffenePersonen', 'rechteEingeschraenktGrund') ? 'is-danger' : ''
        "
        :message="store.getFieldError('betroffenePersonen', 'rechteEingeschraenktGrund')"
      >
        <b-input
          :required="study.betroffenePersonen.rechteEingeschraenkt"
          v-model="study.betroffenePersonen.rechteEingeschraenktGrund"
          @blur="save"
        />
      </b-field>

      <RechtsText v-if="study.betroffenePersonen.rechteEingeschraenkt && showLegal_c4q3">
        {{ $t('rechtstexte.c4q3') }}
      </RechtsText>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { useStudyStore } from '@/stores/studyStore'
import { useInfoToggle, useRechtstextToggle } from '@/composables/useVisibilityToggles'
import InfoText from '@/components/InfoText.vue'
import RechtsText from '@/components/RechtsText.vue'
import { BCheckbox, BField, BIcon, BInput, BNumberinput, BTooltip } from 'buefy'
import AbweichungStandardprozess from '@/components/AbweichungStandardprozess.vue'

defineProps<{ id: string; modelValue?: object }>()

const store = useStudyStore()
const study = store.study
const title = 'Betroffene Personen'

const personenGruppenTypOptions = ['erwachsene', 'minderjaehrige', 'betreute']

const showInfo_c4q0 = useInfoToggle()
const showInfo_c4q2 = useInfoToggle()
const showInfo_c4q3 = useInfoToggle()
const showLegal_c4q0 = useRechtstextToggle()
const showLegal_c4q3 = useRechtstextToggle()

const save = () => {
  store.updateChapter('betroffenePersonen', study.betroffenePersonen)
}

function onRechteEingeschraenktChange() {
  if (!study.betroffenePersonen.rechteEingeschraenkt) {
    study.betroffenePersonen.rechteEingeschraenktGrund = undefined
  }
  save()
}
</script>

<style scoped></style>
